import React from "react";
import styled, { css } from "styled-components";
import { Popover } from "react-tiny-popover";

import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

import {
  DatePicker,
  DatePickerProps
} from "./DatePicker";

export interface DateInputProps extends DatePickerProps {
  label: string;
  disabled?: boolean;
}

export const DateInput: React.ReactFCWithChildren<DateInputProps> = ({
  label,
  date,
  setDate,
  minimumDate,
  maximumDate,
  initialFocusDate,
  disabled = false
}) => {
  const [ pickerVisible, setPickerVisible ] = React.useState(false);

  const onSelectDate = (date: Date) => {
    setDate(date);
    setPickerVisible(false);
  };

  return (
    <Popover
      isOpen={pickerVisible}
      positions={[ "bottom", "right" ]}
      align="start"
      containerStyle={{ zIndex: "1000" }}
      padding={6}
      onClickOutside={() => setPickerVisible(false)}
      content={(
        <DayPickerWrapper>
          <DatePicker
            date={date}
            setDate={onSelectDate}
            minimumDate={minimumDate}
            maximumDate={maximumDate}
            initialFocusDate={initialFocusDate}
          />
        </DayPickerWrapper>
      )}
    >
      <DateInputWrapper
        active={pickerVisible}
        disabled={disabled}
        onClick={!disabled ? () => setPickerVisible(visible => !visible) : undefined}
        className="date-input-wrapper"
      >
        <Icon
          icon={Icons.CALENDAR}
          size={18}
        />

        <DateInputLabel>
          {date?.toLocaleDateString() || label}
        </DateInputLabel>
      </DateInputWrapper>
    </Popover>
  );
};

const DayPickerWrapper = styled.div`
  ${({ theme }) => css`
    padding: 1rem;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius};
    border: 1px solid ${theme.colors.primaryLight};
  `}
`;

const DateInputWrapper = styled.div<{ active: boolean; disabled: boolean; }>`
  ${({
    theme,
    active,
    disabled
  }) => css`
    padding: 1rem 2rem;
    border-radius: ${theme.borderRadius};
    background-color: ${disabled ? `${theme.colors.ghost}` : theme.colors.white};
    border: 1px solid;
    border-color: ${active && !disabled ? theme.colors.primaryLight : theme.colors.lightGrey};
    width: 15rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    transition: 0.25s all ease;

    ${disabled ? css`
      svg {
        fill: ${theme.colors.darkGrey};
      }
      &:hover, & *:hover {
        cursor: not-allowed;
      }
    ` : css`
      &:hover {
        cursor: pointer;
        border-color: ${theme.colors.primaryLight};
      }
    `}
  `}
`;

const DateInputLabel = styled(Text)`
  margin-left: 1rem;
  user-select: none;
`;
